import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { IMAGES } from "../../data";

const ease = [0.76, 0, 0.24, 1];

const Line = ({ children, delay }) => (
  <span className="block overflow-hidden">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const navigate = useNavigate();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);

  return (
    <section ref={ref} id="top" className="relative min-h-screen pt-28 md:pt-0 md:flex md:items-center overflow-hidden" data-testid="hero-section">
      <div className="mx-auto max-w-[1600px] w-full px-6 md:px-12 grid md:grid-cols-12 gap-8 md:gap-6 items-center">
        <div className="md:col-span-7 relative z-10">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.2 }}
            className="text-xs uppercase tracking-[0.35em] text-champagne mb-6 md:mb-10"
          >
            Perth · Australia-wide · Est. 2013
          </motion.p>

          <h1 className="font-serif font-light leading-[0.86] tracking-tight text-[16vw] md:text-[9vw] lg:text-[8.2vw]">
            <Line delay={0.2}>The gown</Line>
            <Line delay={0.35}>you were</Line>
            <Line delay={0.5}><span className="italic text-champagne">always</span> meant</Line>
            <Line delay={0.65}>to wear</Line>
          </h1>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.4, ease }}
            className="mt-8 md:mt-14 flex flex-col sm:flex-row sm:items-center gap-6 md:gap-10 max-w-xl"
          >
            <p className="text-base md:text-lg font-light text-ink/70 leading-relaxed">
              A boutique bridal house crafting couture wedding gowns by appointment — private, unhurried, unforgettable.
            </p>
            <button
              onClick={() => navigate("/collections")}
              data-testid="hero-explore-button"
              className="shrink-0 btn-fill border border-ink px-8 py-4 text-xs uppercase tracking-[0.25em] font-medium hover:text-ivory transition-colors duration-500"
            >
              View Collection
            </button>
          </motion.div>
        </div>

        <div className="md:col-span-5 relative mt-10 md:mt-0">
          <motion.div
            initial={{ clipPath: "inset(100% 0% 0% 0%)" }}
            animate={{ clipPath: "inset(0% 0% 0% 0%)" }}
            transition={{ duration: 1.4, delay: 0.8, ease }}
            className="relative overflow-hidden aspect-[3/4.2] max-h-[80vh] mx-auto max-w-md"
          >
            <motion.img
              src={IMAGES.heroPrimary}
              alt="NK Bridal signature beaded gown"
              style={{ y, scale }}
              className="w-full h-[115%] object-cover object-top"
              data-testid="hero-image"
            />
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.8 }}
            className="absolute -left-2 md:-left-10 bottom-8 bg-ivory px-5 py-4 border border-line"
          >
            <p className="font-serif italic text-xl">Seraphine</p>
            <p className="text-[10px] uppercase tracking-[0.3em] text-ink/50 mt-1">Signature 2026</p>
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 2 }}
        className="hidden md:block absolute bottom-10 left-12 text-[10px] uppercase tracking-[0.3em] text-ink/40"
      >
        Scroll to discover ↓
      </motion.div>
    </section>
  );
};
